import { useContext, useEffect, useState } from "react";
import { authService } from "../../services/authService";
import { Button, Col, Flex, Form, Input, Row, Spin, Typography } from "antd";
import loginPage from "./loginPage.svg";
import XdLogo from "../../assets/XdLogo.svg";
import styles from "./login.module.scss";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthProvider";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faCircleXmark } from "@fortawesome/free-solid-svg-icons";

const Login: React.FC = () => {
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { login, logout, setUserData } = useContext(AuthContext);
    const [form] = Form.useForm();

    useEffect(() => {
        logout();
        // eslint-disable-next-line
    }, []);

    const handleSubmit = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await authService.login(form.getFieldsValue());
            login(response.accessToken);
            const userData = await authService.getUser();
            await setUserData(userData);
            navigate('/Dashboard');
        } catch (err: any) {
            setError(
                err.response?.data
                    ? JSON.stringify(err.response.data, null, 2)
                    : err.message,
            );
        } finally {
            setLoading(false);
        }
    };

    return (
      <>
        <Spin spinning={loading} fullscreen />
        <Row>
          <Col xs={24} md={13} lg={13} className={styles.loginPage}>
            <div className={styles.loginForm} style={{ width: "350px" }}>
              <span className={styles.xdLogo}>
                <img src={XdLogo} alt="icon" />
              </span>
              <Typography.Title level={3} className={styles.welcomeText}>
                Welcome
              </Typography.Title>
              <Typography.Text className={styles.subText}>
                Please enter your details below.
              </Typography.Text>
              <Form
                form={form}
                onFinish={handleSubmit}
                style={{ marginTop: "45px" }}
              >
                <Typography.Title level={5} className={styles.inputTitle}>
                  Email
                </Typography.Title>
                <Form.Item
                  name="email"
                  style={{ marginBottom: "15px" }}
                  validateTrigger={[]}
                  rules={[
                    {
                      type: "email",
                      message: "Enter a valid email address.",
                    },
                    {
                      required: true,
                      message: "Email address is required.",
                    },
                  ]}
                >
                  <Input
                    status={error ? "error" : ""}
                    onChange={(e) => {
                      setError(null);
                      form.setFields([{ name: "email", errors: [] }]);
                    }}
                    className={styles.inputFields}
                  />
                </Form.Item>
                <Typography.Title level={5} className={styles.inputTitle}>
                  Password
                </Typography.Title>
                <Form.Item
                  name="password"
                  rules={[
                    {
                      required: true,
                      message: "Password is required.",
                    },
                  ]}
                  style={{ marginBottom: "4px" }}
                  validateTrigger={[]}
                >
                  <Input.Password
                    status={error ? "error" : ""}
                    onChange={(e) => {
                      setError(null);
                      form.setFields([{ name: "password", errors: [] }]);
                    }}
                    className={styles.inputFields}
                    iconRender={(visible) => (
                      <div>
                        <div className={styles.passwordIcon}>
                          <FontAwesomeIcon
                            style={{ color: "#99A0A7" }}
                            icon={visible ? faEyeSlash : faEye}
                          />
                        </div>
                      </div>
                    )}
                  />
                </Form.Item>
                <Typography.Title level={5} className={styles.forgotPassword}>
                  <Typography.Link
                    href="/forgot-password"
                    className={styles.forgotLink}
                  >
                    Forgot Password
                  </Typography.Link>
                </Typography.Title>
                {error && (
                  <Typography.Text className={styles.errorText}>
                    <FontAwesomeIcon icon={faCircleXmark} />
                    Incorrect email or password. Please try again.
                  </Typography.Text>
                )}
                <Flex vertical gap="small" style={{ width: "100%" }}>
                  <Form.Item shouldUpdate>
                    {() => {
                      const hasErrors = form
                        .getFieldsError()
                        .some(({ errors }) => errors.length);

                      const isTouched = form.isFieldsTouched(true);

                      return (
                        <Button
                          type="primary"
                          block
                          htmlType="submit"
                          disabled={!isTouched || hasErrors}
                          style={{
                            height: "36px",
                            fontWeight: 700,
                            borderRadius: "10px",
                            border: "none",
                          }}
                        >
                          Sign In
                        </Button>
                      );
                    }}
                  </Form.Item>
                </Flex>
              </Form>
              <Typography.Title level={5} className={styles.contactText}>
                Don't have an account?
                <Typography.Link className={styles.contactLink}>
                  {" "}
                  Contact Our Team
                </Typography.Link>
              </Typography.Title>
            </div>
          </Col>
          <Col
            xs={24}
            md={11}
            lg={11}
            style={{
              height: "100vh",
              overflow: "hidden",
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "flex-start",
            }}
          >
            <img
              src={loginPage}
              alt="icon"
              style={{
                display: "block",
                height: "calc(100vh - 40px)",
                width: "auto",
                maxWidth: "calc(100% - 40px)",
                margin: "20px",
                borderRadius: "16px",
                objectFit: "contain",
              }}
            />
          </Col>
        </Row>
      </>
    );
};

export default Login;
